import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
const BASE_URL = "https://www.clearandclean.co.nz";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "",
  featured_image: "", author: "Clear & Clean Team",
  meta_title: "", meta_description: "", is_published: 0,
};

const buildSlug = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").trim();

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("admin_token");
  const authHeader = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const flash = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3000);
  };

  const loadPosts = async () => {
    const res = await fetch(`${API}/?url=blog/all&_t=${Date.now()}`, { headers: authHeader });
    if (res.status === 401) { localStorage.removeItem("admin_token"); navigate("/login"); return; }
    setPosts(await res.json());
  };

  useEffect(() => { loadPosts(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    if (!editId) {
      const slug = buildSlug(title);
      setForm((f) => ({ ...f, title, slug, meta_title: title }));
    } else {
      setForm((f) => ({ ...f, title }));
    }
  };

  const handleSlugChange = (e) => {
    setForm((f) => ({ ...f, slug: buildSlug(e.target.value) }));
  };

  const openCreate = () => { setForm(EMPTY); setEditId(null); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const openEdit = async (post) => {
    setEditId(post.id);
    setShowForm(true);
    setForm({ ...EMPTY, ...post }); // show partial data immediately while fetching
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const res = await fetch(`${API}/?url=blog/posts/${post.id}&_t=${Date.now()}`, { headers: authHeader });
      const full = await res.json();
      if (full && !full.error) setForm(full);
    } catch {
      // keep partial data already set
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isEdit = !!editId;
      const url = isEdit ? `${API}/?url=blog/posts/${editId}` : `${API}/?url=blog/posts`;
      const res = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: authHeader, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        flash(isEdit ? "Post updated!" : "Post created!");
        setShowForm(false); setEditId(null);
        loadPosts();
      } else {
        flash(data.error || "Something went wrong", "danger");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await fetch(`${API}/?url=blog/posts/${id}`, { method: "DELETE", headers: authHeader });
    flash("Post deleted");
    loadPosts();
  };

  const togglePublished = async (post) => {
    await fetch(`${API}/?url=blog/posts/${post.id}`, {
      method: "PUT", headers: authHeader,
      body: JSON.stringify({ ...post, is_published: post.is_published ? 0 : 1 }),
    });
    loadPosts();
  };

  return (
    <div>
      {msg.text && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-semibold mb-0">Blog Posts</h5>
        <button className="btn btn-dark btn-sm" onClick={openCreate}>+ New Post</button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">{editId ? "Edit Post" : "Create New Post"}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Title *</label>
                  <input name="title" className="form-control form-control-sm" value={form.title} onChange={handleTitleChange} required />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Slug (URL)</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text text-muted">/blog/</span>
                    <input name="slug" className="form-control" value={form.slug} onChange={handleSlugChange} required />
                  </div>
                </div>
                <div className="col-md-8">
                  <label className="form-label small fw-semibold">Author</label>
                  <input name="author" className="form-control form-control-sm" value={form.author} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Status</label>
                  <select name="is_published" className="form-select form-select-sm" value={form.is_published} onChange={handleChange}>
                    <option value={0}>Draft</option>
                    <option value={1}>Published</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Featured Image URL</label>
                  <input name="featured_image" className="form-control form-control-sm" value={form.featured_image} onChange={handleChange} placeholder="https://..." />
                  {form.featured_image && (
                    <img src={form.featured_image} alt="preview" style={{ height: 80, marginTop: 8, borderRadius: 6, objectFit: "cover" }} onError={(e) => e.target.style.display = "none"} />
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Excerpt <span className="text-muted fw-normal">(shown on listing page)</span></label>
                  <textarea name="excerpt" className="form-control form-control-sm" rows={2} value={form.excerpt} onChange={handleChange} placeholder="Short summary of the post..." />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Content <span className="text-muted fw-normal">(HTML allowed)</span></label>
                  <textarea name="content" className="form-control form-control-sm" rows={10} value={form.content} onChange={handleChange} placeholder="<p>Full blog post content...</p>" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Meta Title</label>
                  <input name="meta_title" className="form-control form-control-sm" value={form.meta_title} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Meta Description</label>
                  <input name="meta_description" className="form-control form-control-sm" value={form.meta_description} onChange={handleChange} />
                  <small className="text-muted">{(form.meta_description || "").length}/160 chars</small>
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-dark btn-sm" disabled={loading}>
                  {loading ? "Saving…" : editId ? "Update Post" : "Create Post"}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                {form.slug && (
                  <a href={`/blog/${form.slug}`} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm ms-auto">
                    Preview <i className="fa fa-external-link ml-1" />
                  </a>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {posts.length === 0 ? (
            <p className="text-muted p-3 mb-0">No blog posts yet. Create your first one!</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Slug</th>
                    <th>Author</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-semibold">{p.title}</td>
                      <td>
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-decoration-none text-primary small">
                          /blog/{p.slug}
                        </a>
                      </td>
                      <td className="small text-muted">{p.author}</td>
                      <td>
                        <span
                          className={`badge ${p.is_published ? "bg-success" : "bg-secondary"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => togglePublished(p)}
                          title="Click to toggle"
                        >
                          {p.is_published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="text-muted small">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>
                        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(p.id, p.title)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blogs;
